import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../utils/api';
import { TrendingUp, Zap, Shield, ArrowLeft, AlertTriangle, Target, BarChart3, Layers } from 'lucide-react';

const STRATEGY_ICONS = {
  orbis_alpha: <TrendingUp className="w-8 h-8" />,
  orbis_momentum: <Zap className="w-8 h-8" />,
  orbis_defensive: <Shield className="w-8 h-8" />,
  orbis_fibonacci: <Target className="w-8 h-8" />,
  orbis_pattern: <BarChart3 className="w-8 h-8" />,
  orbis_composite: <Layers className="w-8 h-8" />,
};

const STRATEGY_FEATURES = {
  orbis_alpha: ['MA crossover signals', 'RSI confirmation', 'Trend following'],
  orbis_momentum: ['Volatility breakouts', 'Volume confirmation', 'ATR-based stops'],
  orbis_defensive: ['SID Method (RSI+MACD)', 'Supply/Demand zones', 'Conservative entries'],
  orbis_fibonacci: ['0.618 retracement entries', 'Fibonacci extensions', 'Wave analysis'],
  orbis_pattern: ['M-Tops & W-Bottoms', 'Engulfing candles', 'Pin bar reversals'],
  orbis_composite: ['Multi-strategy consensus', 'High confidence trades', 'Risk-adjusted signals'],
};

const StrategySelection = () => {
  const navigate = useNavigate();
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [loadingStrategies, setLoadingStrategies] = useState(true);
  const [botConfig, setBotConfig] = useState({
    allocation_percentage: 10,
    max_daily_loss: 5,
    stop_loss_percentage: 2,
    trading_mode: 'paper',
  });

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [userRes, stratRes] = await Promise.all([
          api.getMe(),
          api.get('/strategies')
        ]);
        setUser(userRes.data);
        const strategiesData = stratRes.data.strategies.map(s => ({
          id: s.id,
          icon: STRATEGY_ICONS[s.id] || <TrendingUp className="w-8 h-8" />,
          name: s.name,
          description: s.description,
          risk: s.risk_level.charAt(0).toUpperCase() + s.risk_level.slice(1),
          features: STRATEGY_FEATURES[s.id] || s.methods,
          min_confidence: s.min_confidence,
          trade_interval: s.trade_interval,
          take_profit_multiplier: s.take_profit_multiplier,
        }));
        setStrategies(strategiesData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
        setLoadingStrategies(false);
      }
    };
    loadAll();
  }, []);

  const handleCreateBot = async () => {
    if (!selectedStrategy) {
      toast.error('Please select a strategy');
      return;
    }

    setLoading(true);
    try {
      await api.createBot({
        strategy: selectedStrategy,
        ...botConfig,
      });
      toast.success('Bot created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bot');
    } finally {
      setLoading(false);
    }
  };

  const canUseLiveTrading = user && (user.subscription_tier === 'pro' || user.subscription_tier === 'elite' || user.subscription_tier === 'owner');

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center">
          <Button
            data-testid="strategy-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <h1 className="text-4xl font-semibold font-['Outfit'] mb-2">Create Trading Bot</h1>
          <p className="text-[#94A3B8] mb-8">Select a strategy and configure your bot</p>
        </motion.div>

        {/* Strategy Selection */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold font-['Outfit'] mb-4">Select Strategy</h2>
          {loadingStrategies ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="bg-[#15171C] border-[#1E293B] animate-pulse">
                  <CardHeader>
                    <div className="w-8 h-8 bg-[#1E293B] rounded mb-4" />
                    <div className="h-6 bg-[#1E293B] rounded w-2/3 mb-2" />
                    <div className="h-4 bg-[#1E293B] rounded w-full" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {strategies.map((strategy, idx) => (
                <motion.div
                  key={strategy.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Card
                    data-testid={`strategy-card-${strategy.id}`}
                    onClick={() => setSelectedStrategy(strategy.id)}
                    className={`cursor-pointer transition-all h-full ${
                      selectedStrategy === strategy.id
                        ? 'bg-[#2F6BFF]/10 border-[#2F6BFF]'
                        : 'bg-[#15171C] border-[#1E293B] hover:border-[#2F6BFF]/50'
                    }`}
                  >
                    <CardHeader>
                      <div className="text-[#2F6BFF] mb-4">{strategy.icon}</div>
                      <CardTitle className="font-['Outfit']">{strategy.name}</CardTitle>
                      <CardDescription className="text-[#94A3B8]">{strategy.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-sm text-xs font-semibold ${
                            strategy.risk === 'High'
                              ? 'bg-red-500/20 text-red-400'
                              : strategy.risk === 'Medium'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-green-500/20 text-green-400'
                          }`}
                        >
                          {strategy.risk} Risk
                        </span>
                        {strategy.take_profit_multiplier && (
                          <span className="inline-block ml-2 px-3 py-1 rounded-sm text-xs font-semibold bg-[#2F6BFF]/20 text-[#2F6BFF]">
                            {strategy.take_profit_multiplier}x TP
                          </span>
                        )}
                      </div>
                      <ul className="space-y-2">
                        {strategy.features.map((feature, fidx) => (
                          <li key={fidx} className="text-sm text-[#94A3B8] flex items-start">
                            <span className="mr-2 text-[#2F6BFF]">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Bot Configuration */}
        {selectedStrategy && (
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardHeader>
                <CardTitle className="font-['Outfit']">Bot Configuration</CardTitle>
                <CardDescription className="text-[#94A3B8]">Set risk parameters for your bot</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Allocation */}
                <div>
                  <Label htmlFor="allocation" className="text-[#F5F7FA] mb-2 block">
                    Allocation Percentage: {botConfig.allocation_percentage}%
                  </Label>
                  <Input
                    data-testid="bot-allocation-input"
                    id="allocation"
                    type="range"
                    min="5"
                    max="100"
                    step="5"
                    value={botConfig.allocation_percentage}
                    onChange={(e) => setBotConfig({ ...botConfig, allocation_percentage: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Percentage of portfolio allocated to this bot
                  </p>
                </div>

                {/* Max Daily Loss */}
                <div>
                  <Label htmlFor="max_loss" className="text-[#F5F7FA] mb-2 block">
                    Max Daily Loss: {botConfig.max_daily_loss}%
                  </Label>
                  <Input
                    data-testid="bot-max-loss-input"
                    id="max_loss"
                    type="range"
                    min="1"
                    max="20"
                    step="1"
                    value={botConfig.max_daily_loss}
                    onChange={(e) => setBotConfig({ ...botConfig, max_daily_loss: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Bot stops trading if daily loss exceeds this threshold
                  </p>
                </div>

                {/* Stop Loss */}
                <div>
                  <Label htmlFor="stop_loss" className="text-[#F5F7FA] mb-2 block">
                    Stop Loss: {botConfig.stop_loss_percentage}%
                  </Label>
                  <Input
                    data-testid="bot-stop-loss-input"
                    id="stop_loss"
                    type="range"
                    min="0.5"
                    max="10"
                    step="0.5"
                    value={botConfig.stop_loss_percentage}
                    onChange={(e) => setBotConfig({ ...botConfig, stop_loss_percentage: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Automatic exit if position drops by this percentage
                  </p>
                </div>

                {/* Trading Mode */}
                <div className="p-4 bg-[#0B0D12] border border-[#1E293B] rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label htmlFor="trading_mode" className="text-[#F5F7FA]">
                        Live Trading Mode
                      </Label>
                      <p className="text-xs text-[#94A3B8] mt-1">
                        {canUseLiveTrading
                          ? 'Execute real trades with actual funds'
                          : 'Requires Pro or Elite subscription'}
                      </p>
                    </div>
                    <Switch
                      data-testid="bot-live-trading-switch"
                      id="trading_mode"
                      checked={botConfig.trading_mode === 'live'}
                      onCheckedChange={(checked) =>
                        setBotConfig({ ...botConfig, trading_mode: checked ? 'live' : 'paper' })
                      }
                      disabled={!canUseLiveTrading}
                    />
                  </div>
                  {botConfig.trading_mode === 'live' && (
                    <div className="mt-3 p-3 bg-[#EF4444]/10 border border-[#EF4444]/50 rounded-sm flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#EF4444] flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-[#EF4444]">
                        Live trading uses real funds. Ensure your exchange API keys are configured correctly.
                      </p>
                    </div>
                  )}
                </div>

                {/* Create Button */}
                <Button
                  data-testid="bot-create-submit-btn"
                  onClick={handleCreateBot}
                  disabled={loading}
                  className="w-full bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
                  size="lg"
                >
                  {loading ? 'Creating Bot...' : 'Create Bot'}
                </Button>
              </CardContent>
            </Card>

            {/* Legal Notice */}
            <div className="mt-6 p-4 bg-[#15171C] border border-[#1E293B] rounded-sm">
              <p className="text-xs text-[#94A3B8] text-center">
                By creating a bot, you acknowledge that trading involves risk. You are in full control and can stop the
                bot at any time.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default StrategySelection;
